import React, { useState, useContext, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';
import { GRADIENTS, COLORS } from './themeConstants';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getAxiosInstance } from '../../utils/axiosInstance';
import { FirebaseContext } from '../../context/FirebaseContext';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { socket } from '../../config';
import FeatureShowcase from './components/FeatureShowcase';
import { NecttosContext } from '../../context/NecttosContext';

const taglines = ['Transforming Administration with Intelligent Innovation', 'Streamline Operations, Amplify Academic Excellence', 'Harness Cutting-Edge Technology for Modern Campuses', 'Innovative Solutions for Progressive Education', 'Driving Efficiency and Inspiring Success', 'Empower Your Institution with Smart Automation'];

const TypeWriter = () => {
	const [currentTagline, setCurrentTagline] = useState(0);
	const [displayText, setDisplayText] = useState('');
	const [isDeleting, setIsDeleting] = useState(false);
	const intervalRef = useRef(null);

	useEffect(() => {
		const typeText = () => {
			const current = taglines[currentTagline];
			if (!isDeleting) {
				if (displayText.length < current.length) {
					setDisplayText(current.slice(0, displayText.length + 1));
				} else {
					setTimeout(() => setIsDeleting(true), 2000);
				}
			} else {
				if (displayText.length > 0) {
					setDisplayText(current.slice(0, displayText.length - 1));
				} else {
					setIsDeleting(false);
					setCurrentTagline((prev) => (prev + 1) % taglines.length);
				}
			}
		};

		intervalRef.current = setInterval(typeText, isDeleting ? 50 : 100);
		return () => clearInterval(intervalRef.current);
	}, [displayText, currentTagline, isDeleting]);

	return (
		<div
			className={`text-sm sm:text-base md:text-lg lg:text-2xl ${COLORS.text.secondary} 
	  leading-relaxed font-medium text-center min-h-[4rem] flex items-center justify-center italic`}>
			<p
				className={`text-lg md:text-xl font-normal text-center mb-12
					bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent`}>
				{displayText}
			</p>
		</div>
	);
};

const WelcomeSection = () => {
	const [phoneNumber, setPhoneNumber] = useState('');
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [otpSent, setOtpSent] = useState(false);
	const [confirm, setConfirm] = useState(null);
	const history = useHistory();
	const [qrCode, setQRCode] = useState(`NECTTOS_QR_${uuidv4()}`);
	const [inputShake, setInputShake] = useState(false);
	const { setCollegeId, setUserId } = useContext(NecttosContext);

	const [isLogin, setIsLogin] = useState(Cookies.get('userToken') !== undefined);

	const style3 = `
		@keyframes gradientAnimation {
		  0% {
			background-position: 0% 50%;
		  }
		  50% {
			background-position: 100% 50%;
		  }
		  100% {
			background-position: 0% 50%;
		  }
		}

		.animated-gradient-text {
		  background: linear-gradient(270deg, #2161ed, #7199f1, #2161ed);
		  background-size: 200% 200%;
		  animation: gradientAnimation 5s ease infinite;
		  -webkit-background-clip: text;
		  -webkit-text-fill-color: transparent;
		  background-clip: text;
		  text-fill-color: transparent;
		}

		.half-gradient-text {
background: linear-gradient(90deg, #0a2c7a 0%, #2161ed 30%, #5278f7 50%, #2161ed 70%, #0a2c7a 100%);
		  background-size: 200% 200%;
		  animation: gradientAnimation 5s ease infinite;
		  -webkit-background-clip: text;
		  -webkit-text-fill-color: transparent;
		  background-clip: text;
		  text-fill-color: transparent;
		}
		  
	  `;
	const mainHeadingStyles = `
	  @keyframes gradientAnimation {
		0% { background-position: 0% 50%; }
		50% { background-position: 100% 50%; }
		100% { background-position: 0% 50%; }
	  }
	
	  @keyframes glowPulse {
		0% { text-shadow: 0 0 0px rgba(33, 97, 237, 0); }
		50% { text-shadow: 0 0 10px rgba(33, 97, 237, 0.4); }
		100% { text-shadow: 0 0 0px rgba(33, 97, 237, 0); }
	  }
	
	  .animated-heading {
		background: linear-gradient(270deg, #2161ed, #7199f1, #2161ed);
		background-size: 400% 400%;
		animation: gradientAnimation 8s ease infinite;
		-webkit-background-clip: text;
		background-clip: text;
		-webkit-text-fill-color: transparent;
		text-fill-color: transparent;
		transition: transform 0.3s ease;
	  }
	
	  .animated-heading:hover {
		animation: gradientAnimation 4s ease infinite, glowPulse 2s ease-in-out infinite;
		transform: scale(1.05);
	  }
	`;

	useEffect(() => {
		const handleQrCode = ({ userDetails, token, collegeId }) => {
			Cookies.set('userToken', token, { expires: 365 * 10, sameSite: 'strict', secure: true });
			localStorage.setItem(
				'userDetails',
				JSON.stringify({
					phoneNumber: userDetails?.phoneNumber,
					username: userDetails?.username,
					displayName: userDetails?.displayName,
					uid: userDetails?.uid,
				}),
			);
			localStorage.setItem('collegeId', JSON.stringify(collegeId));
			setCollegeId(collegeId);
			setUserId(userDetails?.uid);
			toast.success('Login Successfully');
			window.location.href = '/college-list';
		};
		socket.on(qrCode, handleQrCode);
		return () => {
			socket.off(qrCode, handleQrCode);
			socket.disconnect();
		};
	}, []);

	const handleSendOtp = async () => {
		if (phoneNumber.length !== 10) {
			setError('Please enter a valid phone number');
			setInputShake(true);
			setTimeout(() => setInputShake(false), 500);
			return;
		}

		setLoading(true);
		setError(null);
		const instance = getAxiosInstance();
		try {
			const response = await instance.post('/auth/sent-otp', { phoneNumber: `+91${phoneNumber}` });
			setConfirm(response.data.token);
			setOtpSent(true);
			toast.success('OTP Sent');
		} catch (err) {
			setError('Failed to send OTP. Please try again.');
			toast.error('Failed to send OTP');
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyOtp = async () => {
		if (otp.length !== 6) {
			setError('Please enter a valid OTP');
			return;
		}

		setLoading(true);
		setError(null);
		try {
			const instance = getAxiosInstance();
			const response = await instance.post('/auth/verify-otp', { token: confirm, otp });
			Cookies.set('userToken', response.data.jwtToken, { expires: 365 * 10, sameSite: 'strict', secure: true });
			localStorage.setItem(
				'userDetails',
				JSON.stringify({
					phoneNumber: response.data.phoneNumber,
					username: response.data.userName,
					displayName: response.data.userName,
					uid: response.data.uid,
				}),
			);
			setUserId(response.data.uid);
			window.location.href = '/college-list';
		} catch (err) {
			setError('Failed to verify OTP. Please try again.');
			toast.error('Failed to verify OTP');
		} finally {
			setLoading(false);
		}
	};

	return (
		<section id='welcome' className={`relative flex flex-col gap-16 lg:flex-row w-full min-h-screen items-center justify-center pt-2 pb-20 bg-gradient-to-b ${GRADIENTS.background.primary} ${COLORS.text.primary} overflow-hidden`}>
			<div className='absolute inset-0 pointer-events-none'>
				{[...Array(50)].map((_, i) => (
					<svg
						key={i}
						className='absolute animate-floating'
						style={{
							top: `${Math.random() * 100}%`,
							left: `${Math.random() * 100}%`,
							animationDuration: `${Math.random() * 6 + 4}s`,
							animationDelay: `${Math.random() * 3}s`,
							width: `${Math.random() * 15 + 10}px`,
							height: `${Math.random() * 15 + 10}px`,
							fill: 'currentColor',
						}}
						xmlns='http://www.w3.org/2000/svg'
						viewBox='0 0 24 24'>
						<polygon className='text-blue-500 dark:text-blue-400 opacity-60' points='12 2 15 8 22 9 17 14 18 21 12 18 6 21 7 14 2 9 9 8 12 2' />
					</svg>
				))}
			</div>

			<div className='relative z-10 text-center max-w-full md:max-w-4xl mx-4 sm:mx-8 md:mx-16 px-4 sm:px-6 md:px-16 mt-20 lg:mt-0 animate-fadeIn'>
				<h1 className='text-5xl md:text-6xl lg:text-[5rem] font-extrabold leading-tight break-words transition-all duration-300' style={{ wordSpacing: '2.3em' }}>
					<style>{style3}</style>
					<style>{mainHeadingStyles}</style>
					<span className='animated-heading cursor-pointer select-none'>Redefining Campus Excellence</span>
				</h1>

				<div className='mt-8 h-20'>
					<TypeWriter />
				</div>

				<FeatureShowcase />

				<div className='mt-12 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 max-w-sm mx-auto'>
					<button onClick={() => history.push('register')} className={`relative px-8 py-2 text-lg font-semibold bg-gradient-to-r ${GRADIENTS.button.primary} text-white rounded-full shadow-lg hover:shadow-2xl transform transition-all duration-300 hover:scale-105 active:scale-95`}>
						Get Started
					</button>
					<button onClick={() => history.push('know-more')} className={`relative px-8 py-2 text-lg font-semibold text-gray-600 dark:text-gray-300 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transform transition-all duration-300 hover:scale-105 active:scale-95`}>
						Learn More
					</button>
				</div>
			</div>

			<div className={`relative p-8 backdrop-blur-lg ${COLORS.background.glass} transform transition-all duration-500 hover:scale-102 rounded-2xl shadow-2xl border ${COLORS.border.glass} mt-16 md:mt-0 animate-slideUp`}>
				{isLogin ? (
					<div className='w-80 pt-6 flex flex-col items-center'>
						<div className='mb-8 p-4 bg-white rounded-2xl shadow-xl transition-transform duration-300 hover:shadow-2xl'>
							<svg fill='#3276c4' version='1.1' id='Capa_1' xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink' viewBox='0 0 575.783 575.783' xmlSpace='preserve' className='w-16 h-16'>
								<g>
									<path
										d='M487.106,238.621v-38.907c0-26.884-5.271-52.976-15.665-77.549c-10.034-23.725-24.396-45.027-42.685-63.316
											c-18.288-18.289-39.591-32.65-63.315-42.685C340.867,5.771,314.775,0.5,287.892,0.5
											c-43.755,0-85.282,13.919-120.091,40.253c-16.731,12.658-31.305,27.792-43.315,44.983
											c-12.188,17.446-21.444,36.595-27.51,56.917c-3.383,11.336,3.063,23.269,14.399,26.652
											c5.446,1.626,11.028,0.979,15.735-1.387c5.09-2.558,9.158-7.123,10.916-13.013
											c9.425-31.577,29.178-59.984,55.622-79.989c27.307-20.659,59.896-31.579,94.245-31.579
											c41.769,0,81.038,16.266,110.573,45.801s45.801,68.804,45.801,110.573v38.89H90.063
											c-32.058,0-58.14,26.082-58.14,58.141v220.4c0,32.057,26.082,58.139,58.14,58.139h395.658
											c32.059,0,58.14-26.082,58.14-58.139v-220.4C543.86,265.149,518.526,239.36,487.106,238.621z
											M501.021,517.145c0,8.449-6.851,15.299-15.3,15.299H90.063c-8.45,0-15.3-6.85-15.3-15.299v-220.4
											c0-8.449,6.85-15.3,15.3-15.3h395.658c8.449,0,15.3,6.851,15.3,15.3V517.145z'
									/>
									<path
										d='M287.892,303.236c-33.887,0-61.536,27.207-62.256,60.924c-0.01,0.449-0.017,0.898-0.017,1.35
											c0,20.881,10.33,39.393,26.147,50.695c4.363,3.119,9.145,5.686,14.242,7.605v49.07
											c0,11.83,9.59,21.42,21.42,21.42c0.156,0,0.31-0.008,0.464-0.012c5.985-0.127,11.363-2.707,15.172-6.777
											c3.584-3.83,5.782-8.973,5.782-14.631v-48.724c24.06-8.623,41.318-31.654,41.318-58.643
											C350.165,331.172,322.229,303.236,287.892,303.236z M287.892,384.943c-10.733,0-19.433-8.701-19.433-19.434
											s8.7-19.434,19.433-19.434c10.732,0,19.434,8.701,19.434,19.434S298.625,384.943,287.892,384.943z'
									/>
								</g>
							</svg>
						</div>

						<h3 className={`text-2xl font-bold bg-gradient-to-r ${GRADIENTS.text.primary} bg-clip-text text-transparent mb-8 animate-pulse`}>You are logged in.</h3>

						<div className={`w-full space-y-4 ${inputShake ? 'animate-shake' : ''}`}>
							<button
								onClick={() => {
									window.location.href = '/college-list';
								}}
								className='w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 disabled:opacity-50 disabled:cursor-not-allowed'>
								Access your Dashboard
							</button>
						</div>
					</div>
				) : (
					<div className='w-80 pt-6 flex flex-col items-center'>
						<div className='mb-8 p-4 bg-white rounded-2xl shadow-xl transition-transform duration-300 hover:shadow-2xl'>
							<QRCode size={200} className='transition-opacity duration-300 hover:opacity-90' value={qrCode || 'https://necttos.com'} viewBox={`0 0 256 256`} />
						</div>

						<h3 className={`text-2xl font-bold bg-gradient-to-r ${GRADIENTS.text.primary} bg-clip-text text-transparent mb-8 animate-pulse`}>Quick & Secure Login</h3>

						<div className={`w-full space-y-4 ${inputShake ? 'animate-shake' : ''}`}>
							<div className='relative'>
								<input type='number' value={phoneNumber} onChange={(e) => e.target.value.length <= 10 && setPhoneNumber(e.target.value)} placeholder='Phone Number' className={`w-full p-4 rounded-xl border-2 focus:ring-2 focus:ring-blue-400 transition-all duration-300 text-dark ${error ? 'border-red-400' : 'border-gray-200'}`} />
								{error && !otp && <p className='absolute -bottom-6 left-0 text-red-500 text-sm animate-fadeIn'>{error}</p>}
							</div>

							{otpSent ? (
								<>
									<input type='text' value={otp} onChange={(e) => e.target.value.length <= 6 && setOtp(e.target.value)} placeholder='Enter OTP' className='w-full p-4 rounded-xl border-2 focus:ring-2 focus:ring-blue-400 transition-all duration-300 text-dark' />
									{error && otp && <p className='text-red-500 text-sm animate-fadeIn'>{error}</p>}
									<button onClick={handleVerifyOtp} disabled={loading || otp.length !== 6} className='w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 disabled:opacity-50 disabled:cursor-not-allowed'>
										{loading ? (
											<span className='flex items-center justify-center'>
												<svg className='animate-spin h-5 w-5 mr-3' viewBox='0 0 24 24'>
													<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
													<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
												</svg>
												Verifying...
											</span>
										) : (
											'Login'
										)}
									</button>
								</>
							) : (
								<button onClick={handleSendOtp} disabled={loading || phoneNumber.length !== 10} className='w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 disabled:opacity-50 disabled:cursor-not-allowed'>
									{loading ? (
										<span className='flex items-center justify-center'>
											<svg className='animate-spin h-5 w-5 mr-3' viewBox='0 0 24 24'>
												<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
												<path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4z' />
											</svg>
											Sending...
										</span>
									) : (
										'Send OTP'
									)}
								</button>
							)}
						</div>
					</div>
				)}
			</div>
		</section>
	);
};

export default WelcomeSection;
